/**
 * Subscription plan helpers — per-cadence pricing with base price + marginal rate.
 * v2 pricing: each cadence has a price_at_min (includes pickup & delivery) and
 * a marginal per_lb_rate_cents for lbs above the minimum.
 */

export type BillingCadence = "week" | "month" | "quarter";

export type CadencePricing = {
  price_at_min_cents: number;   // total for min_lbs (includes service fee)
  min_lbs: number;
  max_lbs: number;
  per_lb_rate_cents: number;    // marginal above min, AND overage rate
  step_lbs: number;             // input snapping increment
};

// New-sale catalog only. Existing contracts renew from their saved Stripe price
// and subscription terms; never recompute an existing subscriber from this table.
export const CADENCE_PRICING: Record<BillingCadence, CadencePricing> = {
  week:    { price_at_min_cents: 3050,  min_lbs: 12,  max_lbs: 50,  per_lb_rate_cents: 185, step_lbs: 1  },
  month:   { price_at_min_cents: 11400, min_lbs: 48,  max_lbs: 198, per_lb_rate_cents: 185, step_lbs: 5  },
  quarter: { price_at_min_cents: 27500, min_lbs: 120, max_lbs: 600, per_lb_rate_cents: 185, step_lbs: 10 },
};

// Back-compat exports so other files don't need to change:
export const CADENCE_MIN_LBS: Record<BillingCadence, number> = {
  week: CADENCE_PRICING.week.min_lbs,
  month: CADENCE_PRICING.month.min_lbs,
  quarter: CADENCE_PRICING.quarter.min_lbs,
};
export const CADENCE_MAX_LBS: Record<BillingCadence, number> = {
  week: CADENCE_PRICING.week.max_lbs,
  month: CADENCE_PRICING.month.max_lbs,
  quarter: CADENCE_PRICING.quarter.max_lbs,
};

/**
 * Compute the price in cents for a given cadence + lbs combination.
 * The Big Boy has a fixed offer price; other sizes use base + marginal pricing.
 * Throws if lbs is not an integer, is outside [min, max], or is not on a step boundary.
 */
export function computeSubscriptionPrice(cadence: BillingCadence, lbs: number): number {
  if (!Number.isInteger(lbs)) throw new Error("lbs must be an integer");
  const p = CADENCE_PRICING[cadence];
  if (lbs < p.min_lbs) throw new Error(`Minimum for ${cadence}ly is ${p.min_lbs} lbs`);
  if (lbs > p.max_lbs) throw new Error(`Maximum for ${cadence}ly is ${p.max_lbs} lbs`);
  if ((lbs - p.min_lbs) % p.step_lbs !== 0) {
    throw new Error(`${cadence}ly lbs must be in increments of ${p.step_lbs} above the minimum of ${p.min_lbs}`);
  }
  if (isBigBoyQuarterly(cadence, lbs)) return 47900;
  return p.price_at_min_cents + Math.max(0, lbs - p.min_lbs) * p.per_lb_rate_cents;
}

/** Named choices within quarterly billing; custom pound selections remain supported. */
export const QUARTERLY_OPTIONS = [
  { id: "quarterly", name: "Quarterly", lbs: 120 },
  { id: "big-boy", name: "The Big Boy", lbs: 240 },
] as const;
export function quarterlyOffers() {
  return QUARTERLY_OPTIONS.map(option => ({ ...option, price_cents: computeSubscriptionPrice("quarter", option.lbs) }));
}
export function isBigBoyQuarterly(cadence: string, lbs: number) {
  return cadence === "quarter" && lbs === QUARTERLY_OPTIONS[1].lbs;
}

export function cadenceToStripeInterval(cadence: BillingCadence): { interval: "week" | "month"; interval_count: number } {
  if (cadence === "week") return { interval: "week", interval_count: 1 };
  if (cadence === "month") return { interval: "month", interval_count: 1 };
  return { interval: "month", interval_count: 3 }; // quarter
}

// ---------------------------------------------------------------------------
// Legacy plan helpers — retained for backward compatibility while the existing
// billing routes are migrated to the new plan catalog (Tasks 10+).
// ---------------------------------------------------------------------------

/** Legacy plan type union for the old weekly_12lb / biweekly_20lb plans. */
export type PlanType = "weekly_12lb" | "biweekly_20lb";

const LEGACY_PLAN_CONFIG: Record<PlanType, { name: string; lbsPerPeriod: number; lbsHeavyThreshold: number; priceCents: number }> = {
  weekly_12lb: { name: "Weekly 12lb", lbsPerPeriod: 12, lbsHeavyThreshold: 13, priceCents: 2200 },
  biweekly_20lb: { name: "Bi-Weekly 20lb", lbsPerPeriod: 20, lbsHeavyThreshold: 21, priceCents: 3600 },
};

export const isPlanType = (value: unknown): value is PlanType =>
  typeof value === "string" && value in LEGACY_PLAN_CONFIG;

export const planTypeToName = (planType: PlanType | string | null | undefined): string =>
  isPlanType(planType) ? LEGACY_PLAN_CONFIG[planType].name : "";

export const planTypeToLbs = (planType: PlanType | string | null | undefined): number =>
  isPlanType(planType) ? LEGACY_PLAN_CONFIG[planType].lbsPerPeriod : 0;

export const planTypeToHeavyThreshold = (planType: PlanType | string | null | undefined): number =>
  isPlanType(planType) ? LEGACY_PLAN_CONFIG[planType].lbsHeavyThreshold : 13;
